<template>
  <div>
    <SystemDialog
      v-model="dialog"
      persistent
      :max-width="500"
      :title="showBtn ? $t('tip_mod[0]') : $t('tip_mod[1]')"
      :show-close="!!(formData && formData.canSkip)"
      actions-align="center"
      @close="close">
      <p v-if="showBtn" class="system-dialog__tip">
        {{ firstStep ? $t('tip_mod[2]') : $t('tip_mod[3]', [realPhone]) }}
      </p>

      <v-form ref="form_old" v-if="!showBtn">
        <div class="system-dialog__field">
          <div class="system-dialog__label">
            <RequiredDot>{{ $t('old_phone[0]') }}</RequiredDot>
          </div>
          <v-text-field
            v-model="old_phone_number"
            prefix="+86"
            dense
            outlined
            clearable
            hide-details="auto"
            validate-on-blur
            class="system-dialog__input"
            :rules="[rules.required, rules.cn_phone]"
            required></v-text-field>
        </div>
      </v-form>

      <v-tabs-items v-model="tab" v-if="showBtn">
        <v-tab-item :value="1">
          <v-form ref="form_phone">
            <div class="system-dialog__field">
              <div class="system-dialog__label">
                <RequiredDot>
                  {{ showBtn ? $t('user.phone_number') : $t('old_phone[1]') }}
                </RequiredDot>
              </div>
              <NumberInput
                v-model="phone_number"
                dense
                outlined
                show-details
                :rules="phoneRules"
                class="bind-phone-number"></NumberInput>
            </div>
          </v-form>
        </v-tab-item>
        <v-tab-item :value="2">
          <v-form ref="form_code">
            <div class="system-dialog__field bind-phone-code">
              <div class="system-dialog__label">
                <RequiredDot>{{ $t('tip_code[0]') }}</RequiredDot>
              </div>
              <div class="bind-phone-code__row">
                <v-text-field
                  v-model="formData.code"
                  dense
                  outlined
                  clearable
                  hide-details="auto"
                  validate-on-blur
                  class="system-dialog__input bind-phone-code__input"
                  :rules="[rules.required, codeRule]"
                  @change="codeError = true"></v-text-field>
                <v-btn
                  v-if="!codeLoading"
                  depressed
                  small
                  outlined
                  class="text-none bind-phone-code__btn"
                  color="primary"
                  :loading="btnLoading"
                  @click="fetchCode">
                  {{ $t('tip_code[1]') }}
                </v-btn>
                <v-btn
                  v-else
                  depressed
                  small
                  outlined
                  disabled
                  class="text-none bind-phone-code__btn"
                  color="primary">
                  {{ `${codeTime}s` }}
                </v-btn>
              </div>
            </div>
          </v-form>
        </v-tab-item>
      </v-tabs-items>

      <template v-slot:actions>
        <v-btn
          v-if="formData && formData.canSkip"
          depressed
          small
          class="text-none"
          color="primary"
          outlined
          @click="close">
          {{ $t('button.cancel') }}
        </v-btn>
        <v-btn
          v-if="showBtn && firstStep"
          depressed
          small
          class="text-none"
          color="primary"
          @click="handleNext">
          {{ $t('button.next') }}
        </v-btn>
        <v-btn
          v-if="!showBtn || !firstStep"
          depressed
          small
          class="text-none"
          color="primary"
          :loading="loading"
          @click="ok">
          {{ $t('button.submit') }}
        </v-btn>
      </template>
    </SystemDialog>

    <UpdateBillingInfo ref="updateBillingInfo"></UpdateBillingInfo>
  </div>
</template>

<script>
import loadingMixin from '@/module/mixins/loading.mixin.js'
import RequiredDot from '@/components/RequiredDot.vue'
import rules from '@/module/rules/index.js'
import { mapActions, mapState } from 'vuex'
import NumberInput from '@/components/NumberInput/index.vue'
import UpdateBillingInfo from '@/views/_components/BillingInfo/update/index.vue'
import SystemDialog from '@/components/SystemDialog.vue'

export default {
  name: 'BindPhoneConfirm',
  i18n: {
    sharedMessages: {
      en: {
        old_phone: ['Old Phone Number', 'New Phone Number'],
        tip_mod: [
          'Bind Phone Number',
          'Change Phone Number',
          'Please Enter Your Phone Number to Verify Information',
          'Verification Code Has Been Sent to Your Phone: {0}, Please Check'
        ],
        tip_code: ['Verification Code', 'Get Code', 'Invalid Verification Code Or Phone Number'],
        tip_message: ['Successfully Bound Phone Number', 'Successfully Change Phone Number'],
        skip: 'Skip'
      },
      zh_CN: {
        old_phone: ['原手机号码', '新手机号码'],
        tip_mod: [
          '绑定手机',
          '换绑手机',
          '请输入您的手机号码验证信息',
          '验证码已发送至您的手机：{0}，请注意查收'
        ],
        tip_code: ['验证码', '获取验证码', '无效的验证码或手机号'],
        tip_message: ['成功绑定手机', '换绑手机成功'],
        skip: '跳过'
      }
    }
  },
  components: { SystemDialog, UpdateBillingInfo, NumberInput, RequiredDot },
  mixins: [loadingMixin],
  data() {
    return {
      rules,
      codeLoading: false,
      btnLoading: false,
      codeTime: 60,
      codeError: true,
      old_phone_number: '',
      phone_number: '',
      callback: null,
      tab: 1
    }
  },
  computed: {
    showBtn() {
      return !this.clientInfo.is_verified_phone
    },
    firstStep() {
      return this.tab === 1
    },
    realPhone() {
      return this.phone_number.replace('cn', '').trim()
    },
    phoneRules() {
      return this.phone_number.startsWith('cn')
        ? [rules.required, rules.cn_phone]
        : [rules.canNotHTMLTag, rules.required]
    },
    ...mapState('client', ['clientInfo'])
  },
  methods: {
    resetForm() {
      this.$refs.form_old && this.$refs.form_old.resetValidation()
      this.$refs.form_phone && this.$refs.form_phone.resetValidation()
      this.$refs.form_code && this.$refs.form_code.resetValidation()
      this.btnLoading = false
      this.codeError = true
    },
    codeRule() {
      return this.codeError || this.$t('tip_code[2]')
    },
    open(callback, canSkip = false) {
      this.formData = Object.assign({
        code: '',
        canSkip: canSkip
      })
      this.callback = callback || null
      this.old_phone_number = ''
      this.phone_number = ''
      this.resetForm()
      this.tab = 1
      this.dialog = true
    },
    skipHandler() {
      this.callback && this.callback()
      this.dialog = false
    },
    fetchCode() {
      this.btnLoading = true
      return this.$http
        .put(`/clients/${this.clientInfo.id}/send_phone_verification_code/`, {
          phone_code: 'cn',
          phone_number: this.realPhone
        })
        .then(() => {
          this.btnLoading = false
          this.codeTime = 60
          this.codeLoading = true
          this.countDown()
        })
        .catch((e) => {
          this.btnLoading = false
          this.$message.error(e.detail)
          return Promise.reject(e)
        })
    },
    countDown() {
      setTimeout(() => {
        if (this.codeTime > 0) {
          this.codeTime -= 1
          this.countDown()
        } else {
          this.codeLoading = false
        }
      }, 1000)
    },
    bindNumber() {
      return this.$http
        .put(`/clients/${this.clientInfo.id}/bind_phone_number/`, {
          phone_code: 'cn',
          phone_number: this.realPhone,
          verification_code: this.formData.code
        })
        .then(({ return_code }) => {
          if (return_code === 0) {
            this.getClientInfo(true)
            this.$message.success(this.$t('tip_message[0]'))
            return Promise.resolve()
          } else {
            this.codeError = false
            this.$refs.form_code.validate()
            return Promise.reject()
          }
        })
        .catch((e) => {
          this.loading = false
          this.$message.error(e.detail)
          return Promise.reject(e.detail)
        })
    },
    changeNumber() {
      return this.$http
        .put(`/clients/${this.clientInfo.id}/change_phone_number/`, {
          phone_code: 'cn',
          old_phone_number: this.old_phone_number,
          phone_number: this.realPhone,
          verification_code: this.formData.code
        })
        .then(({ return_code }) => {
          if (return_code === 0) {
            this.getClientInfo(true)
            this.$message.success(this.$t('tip_message[1]'))
            return Promise.resolve()
          } else {
            this.codeError = false
            this.$refs.form_code.validate()
            return Promise.reject()
          }
        })
        .catch((e) => {
          this.loading = false
          this.$message.error(e.detail)
          return Promise.reject(e.detail)
        })
    },
    ok() {
      if (
        (this.showBtn || this.$refs.form_old.validate()) &&
        this.$refs.form_phone.validate() &&
        this.$refs.form_code.validate()
      ) {
        let done = () => {
          this.loading = false
          this.dialog = false
          this.callback && this.callback()
        }
        this.loading = true
        this[this.showBtn ? 'bindNumber' : 'changeNumber']()
          .then(() => {
            done()
          })
          .catch(() => {
            this.loading = false
          })
      }
    },
    handleNext() {
      this.$nextTick(() => {
        if (this.$refs.form_phone.validate()) {
          if (this.phone_number.startsWith('cn')) {
            if (!this.btnLoading) {
              this.fetchCode().then(() => {
                this.tab = 2
              })
            }
          } else {
            this.$refs.updateBillingInfo.open(this.callback, true, true, {
              phone_number: this.phone_number
            })
            this.close()
          }
        }
      })
    },
    ...mapActions('client', ['getClientInfo'])
  }
}
</script>

<style lang="scss">
// NumberInput 根节点不是 v-text-field，这里把系统弹窗输入外观落到内部控件上
.system-dialog .bind-phone-number.n-box--outlined {
  .v-text-field--outlined > .v-input__control > .v-input__slot {
    background: #fff !important;
    min-height: 40px;
  }

  .v-text-field--outlined fieldset {
    border-color: #d7dde8 !important;
    border-width: 1px !important;
  }

  .v-text-field--outlined:hover:not(.v-input--is-focused):not(.v-input--is-disabled) fieldset {
    border-color: #c5ccd8 !important;
  }

  .v-input--is-focused.v-text-field--outlined fieldset {
    border-color: var(--v-primary-base) !important;
  }

  .v-input--is-focused.v-text-field--outlined > .v-input__control > .v-input__slot {
    box-shadow: 0 0 0 3px rgba(17, 24, 39, 0.06);
  }

  input {
    font-size: 13px;
    color: #374151;
    font-weight: 500;
  }
}

.bind-phone-code {
  &__row {
    display: flex;
    align-items: flex-start;
    gap: 10px;
  }

  &__input {
    flex: 1 1 auto;
    min-width: 0;
  }

  &__btn {
    flex: 0 0 auto;
    margin-top: 0 !important;
    height: 40px !important;
    min-width: 96px !important;
  }
}
</style>
